import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useEvent, useIsMounted } from "@ardoq/hooks";
import { useState, useEffect, useCallback, useLayoutEffect, forwardRef, useRef } from "react";
import styled, { css } from "styled-components";
import { colors, s8, s16, s4, r4, shadowM, timeFastMs } from "@ardoq/design-tokens";
import { caption, text1, button, header5 } from "@ardoq/typography";
import { ICON_SELECTOR, IconSize, CheckMarkIcon, Icon, IconName, NoneIcon } from "@ardoq/icons";
import { Space } from "@ardoq/style-helpers";
import { FIELD_SELECTOR } from "@ardoq/forms";
import { FlexBox, Stack } from "@ardoq/layout";
import { isString } from "lodash";
import { WithPopover, PopoverPlacement } from "@ardoq/popovers";
import { MeasureStyledText } from "@ardoq/dom-utils";
import { CustomOption } from "@ardoq/custom-select-options";
var DropdownSize = /* @__PURE__ */ ((DropdownSize2) => {
  DropdownSize2[DropdownSize2["S"] = 1] = "S";
  DropdownSize2[DropdownSize2["M"] = 2] = "M";
  DropdownSize2[DropdownSize2["FULL_WIDTH"] = 3] = "FULL_WIDTH";
  DropdownSize2[DropdownSize2["AUTO"] = 4] = "AUTO";
  return DropdownSize2;
})(DropdownSize || {});
var DropdownButtonOptionType = /* @__PURE__ */ ((DropdownButtonOptionType2) => {
  DropdownButtonOptionType2["DEFAULT"] = "DEFAULT";
  DropdownButtonOptionType2["DANGER"] = "DANGER";
  return DropdownButtonOptionType2;
})(DropdownButtonOptionType || {});
const composeCssColorBlock = ({
  color,
  backgroundColor,
  iconColor
}) => css`
  ${color && css`
    color: ${color};
  `};
  ${backgroundColor && css`
    background-color: ${backgroundColor};
  `}
  ${(iconColor || color) && css`
    ${ICON_SELECTOR} {
      color: ${iconColor || color};
    }
  `}
`;
const composeCssStatesColorBlock = (colorMap) => css`
  ${composeCssColorBlock(colorMap.default)}
  &:hover {
    ${composeCssColorBlock(colorMap.hover)}
  }
`;
const getDropdownWidth = (dropdownSize) => {
  if (dropdownSize === DropdownSize.M) return "448px";
  if (dropdownSize === DropdownSize.S) return "224px";
  if (dropdownSize === DropdownSize.FULL_WIDTH) return "100%";
  return "auto";
};
const OptionDescription = styled.div`
  ${caption};
  color: ${colors.textSubtle};
  ${({ $isDisabled }) => $isDisabled && css`
      color: ${colors.textDisabled};
    `};
  ${({ $shouldTruncate }) => $shouldTruncate && threeLineEllipsisCSS}
`;
const OptionLabel = styled.div`
  ${text1};
  color: ${(props) => props.$isHighlighted ? colors.textOnInvertedStrong : "inherit"};
  ${({ $shouldTruncate }) => $shouldTruncate && twoLineEllipsisCSS}
  ${({ $shouldTruncate, $maxTextLines = 2 }) => $shouldTruncate && css`
      -webkit-line-clamp: ${$maxTextLines};
    `}
    ${({ $hideLabel }) => $hideLabel && css`
      display: none;
    `}
`;
const twoLineEllipsisCSS = css`
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  word-break: break-word;
`;
const threeLineEllipsisCSS = css`
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  word-break: break-word;
`;
const menuOptionActiveCSS = css`
  background-color: ${colors.surfaceDefaultSubtle};
`;
const menuOptionSelectedCSS = css`
  &&& {
    background-color: ${colors.surfaceActive};
  }
`;
const menuOptionHighlightedCSS = css`
  background-color: ${colors.surfaceActionPrimary};
  &:hover {
    background-color: ${colors.surfaceActionPrimaryHover};
  }
`;
const DEFAULT_COLOR_MAP = {
  default: {},
  hover: {}
};
const DANGER_COLOR_MAP = {
  default: {
    color: colors.textError,
    iconColor: colors.iconError
  },
  hover: {
    backgroundColor: colors.surfaceError
  }
};
const DROPDOWN_BUTTON_TYPE_COLOR_MAP = {
  [DropdownButtonOptionType.DEFAULT]: DEFAULT_COLOR_MAP,
  [DropdownButtonOptionType.DANGER]: DANGER_COLOR_MAP
};
const MenuOptionWrapper = styled.div`
  display: flex;
  padding: ${s8} ${s16};
  min-height: 40px;
  justify-content: center;
  align-items: center;
  gap: ${s8};
  word-break: break-word;
  color: ${colors.textDefault};
  background-color: ${colors.surfaceDefault};

  ${OptionDescription} {
    margin-top: ${s4};
  }

  /* if isHovered is provided (true | false) then we don't
     use the CSS :hover - this feature is used on keyboard
     navigation menus (like Select) */
  ${({ $isHovered, $isClickable }) => $isClickable && $isHovered === void 0 && css`
        cursor: pointer;
        &:hover {
          ${menuOptionActiveCSS};
        }
      ` || $isHovered === true && css`
        cursor: pointer;
        ${menuOptionActiveCSS};
      `}

  ${({ $isSelected }) => $isSelected && menuOptionSelectedCSS}

  ${({ $isFocused }) => $isFocused && menuOptionActiveCSS}

  ${({ $isDisabled }) => $isDisabled && css`
      pointer-events: none;
      color: ${colors.textDisabled};
      /* We have cases where we use a custom img or multicolored svgs in
       * we highlight them as disabled add a opacity, but if the svg is
       * an ardoq icon we revert the opacity and set icon disabled color instead. */
      & > img,
      & > svg {
        opacity: 0.3;
      }
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
        opacity: inherit;
      }
    `}

  ${({ $isHighlighted }) => $isHighlighted && menuOptionHighlightedCSS}

  ${({ $buttonType }) => $buttonType && composeCssStatesColorBlock(DROPDOWN_BUTTON_TYPE_COLOR_MAP[$buttonType])}
`;
MenuOptionWrapper.toString();
const MenuOptionInnerWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const MenuOptionOuterWrapper = styled.div`
  flex: 1;
  position: relative;
`;
const MenuOptionOuterWrapperWithContent = styled.div`
  position: relative;
`;
const ActiveContentWrapper = styled.div`
  position: absolute;
  right: ${s16};
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
`;
const MENU_OPTION_SELECTOR = MenuOptionOuterWrapper.toString();
const MenuOptionBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${s8};

  ${ICON_SELECTOR} {
    flex: 0 ${IconSize.REGULAR}px;
  }

  ${OptionLabel} {
    flex: 1 1 auto;
  }

  ${({ $align }) => $align === "center" && css`
        justify-content: center;
        ${OptionLabel}, ${ICON_SELECTOR} {
          flex: 0 auto;
        }
      ` || $align === "right" && css`
        justify-content: flex-end;
        ${OptionLabel}, ${ICON_SELECTOR} {
          flex: 0 auto;
        }
      `}
`;
MenuOptionBody.displayName = "MenuOptionBody";
const MENU_OPTION_BODY_SELECTOR = MenuOptionBody.toString();
const DropdownMenuSectionSplit = styled.div`
  border-bottom: 1px solid ${colors.borderAction};
  height: 1px;
  margin: ${s4} 0;
`;
DropdownMenuSectionSplit.displayName = "DropdownMenuSectionSplit";
const wrappersSpacingCSS = css`
  & > ${OptionDescription} {
    margin: ${s4} ${s16};
  }

  & > ${MENU_OPTION_SELECTOR} {
    margin-top: ${s4};
    &:first-child {
      margin-top: 0;
    }
  }
`;
const DropdownMenuSectionWrapper = styled.div`
  background-color: ${colors.surfaceDefault};
  color: ${colors.textDefault};
  ${wrappersSpacingCSS};
  &:last-child {
    ${DropdownMenuSectionSplit}:last-child {
      display: none;
    }
  }
`;
DropdownMenuSectionWrapper.displayName = "DropdownMenuSectionWrapper";
const DropdownMenuBodyWrapper = styled.div`
  ${wrappersSpacingCSS};
  padding: ${s4} 0;
  overflow: auto;

  & > ${DropdownMenuSectionSplit}:last-child {
    display: none;
  }
`;
const dropdownMenuWrapperCSS = css`
  border-radius: ${r4};
  ${({
  $dropdownSize,
  $minDropdownSize,
  $maxDropdownSize,
  $maxDropdownHeight
}) => `
    width: ${getDropdownWidth($dropdownSize)};
    ${$minDropdownSize ? `min-width: ${getDropdownWidth($minDropdownSize)};` : ""}
    ${$maxDropdownSize ? `max-width: ${getDropdownWidth($maxDropdownSize)};` : ""}
    max-height: ${$maxDropdownHeight ?? 300}px;
  `};
  overflow-y: auto;
  box-shadow: ${shadowM};
  background-color: ${colors.surfaceDefault};
`;
const DropdownMenuWrapper = styled(Space).attrs({
  $isVertical: true,
  $gap: "none"
})`
  ${dropdownMenuWrapperCSS};
  overflow: hidden;

  /* ANIMATION */
  transition:
    opacity ${timeFastMs},
    transform ${timeFastMs};
  ${({ $isVisible }) => ({
  undefined: css``,
  true: css`
        opacity: 1;
      `,
  false: css`
        opacity: 0;
        transform: translateY(-20px);
      `
})[`${$isVisible}`]}
`;
const DropdownActionStyle = styled(FlexBox).attrs({
  gap: "medium"
})`
  ${button}
  cursor: pointer;
  color: ${colors.textModerate};
  &:hover {
    color: ${colors.textDefault};
  }
`;
const DropdownFooterActionSpace = styled(DropdownActionStyle)`
  flex: 1;
  padding: ${s8} ${s16};
`;
const DropdownMenuHeaderWrapper = styled(Stack).attrs({
  gap: "medium"
})`
  &:first-child {
    border-bottom: 1px solid ${colors.borderAction};
  }
  &:last-child {
    border-top: 1px solid ${colors.borderAction};
  }

  ${FIELD_SELECTOR} {
    padding: ${s16};
  }
`;
const DropdownMenuSectionTitle = styled.div`
  ${header5};
  color: ${colors.textSubtle};
  padding: ${s8} ${s16} 0;
  &:last-child {
    padding-top: ${s16};
  }
  ${({ $shouldTruncate }) => $shouldTruncate && twoLineEllipsisCSS}
`;
const OptionCheckIcon = styled(CheckMarkIcon)`
  color: ${colors.iconActionActive};
`;
const OptionIcon = styled(Icon)`
  color: ${({ $isHighlighted, $color }) => {
  if ($color) return $color;
  return $isHighlighted ? colors.iconOnInvertedStrong : colors.icon;
}};
`;
const CheckboxWrapper = styled.div`
  /* This feels a bit brutal, but we want to make sure that
    regular option (with or without checkbox) has equal height (40px)*/
  height: 20px;
  align-self: start;
`;
function useElementSize() {
  const [ref, setRef] = useState(null);
  const [size, setSize] = useState({
    width: 0,
    height: 0
  });
  const handleSize = useCallback(() => {
    setSize({
      width: (ref == null ? void 0 : ref.offsetWidth) || 0,
      height: (ref == null ? void 0 : ref.offsetHeight) || 0
    });
  }, [ref == null ? void 0 : ref.offsetHeight, ref == null ? void 0 : ref.offsetWidth]);
  const resizeEvent = useEvent("resize");
  useEffect(() => {
    handleSize();
  }, [resizeEvent]);
  useLayoutEffect(() => {
    handleSize();
  }, [ref == null ? void 0 : ref.offsetHeight, ref == null ? void 0 : ref.offsetWidth]);
  return [setRef, size];
}
const Container = styled.div`
  display: flex;
  align-items: center;
  ${caption};
  color: ${colors.textSubtle};
  margin-top: ${s4};
  overflow: hidden;
`;
const Breadcrumb = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const CalculatedBreadcrumb = styled(Breadcrumb)`
  max-width: ${(props) => props.$maxBreadcrumbWidth}px;
`;
const AllBreadcrumbs = ({ breadcrumbs }) => {
  var _a, _b;
  return /* @__PURE__ */ jsxs(Container, { style: { flexWrap: "wrap", maxWidth: 448 }, children: [
    /* @__PURE__ */ jsx(FlexBox, { marginRight: "xsmall", children: /* @__PURE__ */ jsx(
      Icon,
      {
        color: colors.icon,
        iconSize: IconSize.SMALL,
        iconName: ((_a = breadcrumbs[0]) == null ? void 0 : _a.icon) ? (_b = breadcrumbs[0]) == null ? void 0 : _b.icon : IconName.FOLDER
      }
    ) }),
    breadcrumbs.map(({ text }, i) => {
      return /* @__PURE__ */ jsx(FlexBox, { align: "center", children: i === breadcrumbs.length - 1 ? /* @__PURE__ */ jsx(Breadcrumb, { children: text }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Breadcrumb, { children: text }),
        /* @__PURE__ */ jsx(
          Icon,
          {
            iconSize: IconSize.SMALL,
            iconName: IconName.CHEVRON_RIGHT
          }
        )
      ] }) }, text + i);
    })
  ] });
};
const ICONS_WIDTH = IconSize.SMALL * 4;
const getTextWidth = new MeasureStyledText().getTextWidth;
const ICON_SIZE = IconSize.SMALL + 4;
const Breadcrumbs = ({ breadcrumbs }) => {
  var _a, _b;
  const [breadcrumbsToRender, setBreadcrumbsToRender] = useState(
    Array(breadcrumbs.length).fill(true)
  );
  const [maxBreadcrumbWidth, setMaxBreadcrumbWidth] = useState(0);
  const [ref, { width: containerWidth }] = useElementSize();
  useEffect(() => {
    if (breadcrumbs.length === 1) {
      setMaxBreadcrumbWidth(containerWidth - ICONS_WIDTH);
      return;
    }
    const updatedMaxBreadcrumbWidth = (containerWidth - ICON_SIZE * 4) / 2;
    setMaxBreadcrumbWidth(updatedMaxBreadcrumbWidth);
    const firstBreadcrumbsWidth = Math.min(
      getTextWidth({ text: breadcrumbs[0].text }),
      updatedMaxBreadcrumbWidth
    ) + ICON_SIZE;
    const lastBreadcrumbsWidth = Math.min(
      getTextWidth({ text: breadcrumbs[breadcrumbs.length - 1].text }),
      updatedMaxBreadcrumbWidth
    );
    const breadcrumbsState = breadcrumbs.reduce(
      (acc, breadcrumb, i) => {
        if (i === 0 || i === breadcrumbs.length - 1) {
          acc.breadcrumbsToRender[i] = true;
          return acc;
        }
        const currentTotalWidth = Math.min(
          getTextWidth({ text: breadcrumb.text }),
          updatedMaxBreadcrumbWidth
        ) + acc.totalWidth + ICON_SIZE;
        if (currentTotalWidth < containerWidth && acc.breadcrumbsToRender[i - 1]) {
          acc.totalWidth = currentTotalWidth;
          acc.breadcrumbsToRender[i] = true;
          return acc;
        }
        if (acc.breadcrumbsToRender[i - 1]) {
          acc.totalWidth += ICON_SIZE * 2;
        }
        acc.breadcrumbsToRender[i] = false;
        return acc;
      },
      {
        totalWidth: ICON_SIZE + firstBreadcrumbsWidth + lastBreadcrumbsWidth,
        breadcrumbsToRender
      }
    );
    setBreadcrumbsToRender(breadcrumbsState.breadcrumbsToRender);
  }, [
    breadcrumbs,
    containerWidth,
    breadcrumbsToRender,
    setBreadcrumbsToRender
  ]);
  return /* @__PURE__ */ jsxs(Container, { ref, children: [
    /* @__PURE__ */ jsx(FlexBox, { marginRight: "xsmall", children: /* @__PURE__ */ jsx(
      Icon,
      {
        color: colors.icon,
        iconSize: IconSize.SMALL,
        iconName: ((_a = breadcrumbs[0]) == null ? void 0 : _a.icon) ? (_b = breadcrumbs[0]) == null ? void 0 : _b.icon : IconName.FOLDER
      }
    ) }),
    breadcrumbs.map(({ text }, i) => {
      if (i > 0 && !breadcrumbsToRender[i] && !breadcrumbsToRender[i - 1]) {
        return null;
      }
      return /* @__PURE__ */ jsx(FlexBox, { align: "center", children: i === breadcrumbs.length - 1 ? /* @__PURE__ */ jsx(CalculatedBreadcrumb, { $maxBreadcrumbWidth: maxBreadcrumbWidth, children: text }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        breadcrumbsToRender[i] ? /* @__PURE__ */ jsx(
          CalculatedBreadcrumb,
          {
            $maxBreadcrumbWidth: maxBreadcrumbWidth,
            children: text
          }
        ) : /* @__PURE__ */ jsx(
          WithPopover,
          {
            content: () => /* @__PURE__ */ jsx(AllBreadcrumbs, { breadcrumbs }),
            children: /* @__PURE__ */ jsx(FlexBox, { marginRight: "xsmall", children: /* @__PURE__ */ jsx(
              Icon,
              {
                style: { transform: "rotate(90deg)" },
                iconSize: IconSize.SMALL,
                iconName: IconName.MORE_VERT
              }
            ) })
          }
        ),
        /* @__PURE__ */ jsx(
          Icon,
          {
            iconSize: IconSize.SMALL,
            iconName: IconName.CHEVRON_RIGHT
          }
        )
      ] }) }, text + i);
    })
  ] });
};
const getIconProps = ({
  leftIconName,
  rightIconName,
  leftIcon,
  rightIcon
}, side) => {
  if (side === "left") {
    return leftIconName ? {
      name: leftIconName
    } : leftIcon ? leftIcon : void 0;
  }
  return rightIconName ? {
    name: rightIconName
  } : rightIcon ? rightIcon : void 0;
};
const dropdownMenuOperations = {
  getIconProps
};
const MAX_TEXT_LINES = 2;
const isTruncated = (textNode, maxTextLines) => {
  const textHeight = textNode.scrollHeight || 0;
  const textLineHeight = window.getComputedStyle(textNode).lineHeight;
  const lineHeight = parseInt(textLineHeight, 10);
  if (!Number.isInteger(lineHeight)) {
    return false;
  }
  return Math.floor(textHeight / lineHeight) > maxTextLines;
};
const DropdownMenuOption = forwardRef(
  (props, ref) => props.customData ? /* @__PURE__ */ jsx(DropdownMenuOptionWithContent, { ...props, ref }) : /* @__PURE__ */ jsx(DropdownMenuOptionWithOptions, { ...props, ref })
);
const DropdownMenuOptionWithOptions = forwardRef((props, ref) => {
  const {
    label,
    breadcrumbs,
    leftContent,
    rightContent,
    description,
    isDisabled,
    isFocused,
    isHovered,
    isSelected,
    align = "justified",
    isChecked,
    children,
    truncateDescription,
    ariaLabel,
    clickId,
    testId,
    intercomTarget,
    isHighlighted,
    keepRightContentActive,
    truncateTitle,
    popoverProps,
    buttonType,
    hideSelectedOptions,
    onClick,
    menuOptionWrapperProps
  } = props;
  const labelRef = useRef(null);
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);
  const isStringLabel = isString(label);
  useEffect(() => {
    var _a;
    setIsLabelTruncated(
      Boolean(
        truncateTitle && isStringLabel && labelRef.current && ((_a = labelRef.current) == null ? void 0 : _a.scrollHeight) && isTruncated(labelRef.current, MAX_TEXT_LINES)
      )
    );
  }, [label, isStringLabel, truncateTitle]);
  const leftIcon = dropdownMenuOperations.getIconProps(props, "left");
  const rightIcon = dropdownMenuOperations.getIconProps(props, "right");
  const hasCheckbox = isChecked !== void 0;
  const content = /* @__PURE__ */ jsxs(MenuOptionOuterWrapper, { children: [
    /* @__PURE__ */ jsxs(
      MenuOptionWrapper,
      {
        role: "option",
        "aria-selected": hasCheckbox ? isChecked : void 0,
        "aria-checked": hasCheckbox ? isChecked : void 0,
        "aria-label": ariaLabel,
        ref,
        $hasLeftIcon: !!leftIcon,
        $isDisabled: isDisabled,
        $isFocused: isFocused,
        $isHovered: isHovered,
        $isSelected: isSelected,
        $isClickable: Boolean(onClick),
        "data-click-id": clickId,
        "data-test-id": testId,
        "data-intercom-target": intercomTarget || label,
        $isHighlighted: isHighlighted,
        $buttonType: buttonType,
        onClick: !isDisabled ? onClick : void 0,
        ...menuOptionWrapperProps,
        children: [
          leftContent,
          hasCheckbox && !hideSelectedOptions && // if hideSelectOptions, we will never show a check mark here. So this part just becomes unnecessary whitespace
          /* @__PURE__ */ jsxs(CheckboxWrapper, { children: [
            isChecked === true && /* @__PURE__ */ jsx(OptionCheckIcon, {}),
            isChecked === false && /* @__PURE__ */ jsx(NoneIcon, {})
          ] }),
          /* @__PURE__ */ jsxs(MenuOptionInnerWrapper, { children: [
            /* @__PURE__ */ jsxs(MenuOptionBody, { $align: align, children: [
              leftIcon && /* @__PURE__ */ jsx(
                OptionIcon,
                {
                  iconName: leftIcon.name,
                  $isHighlighted: isHighlighted,
                  $color: leftIcon.color
                }
              ),
              /* @__PURE__ */ jsx(
                WithPopover,
                {
                  content: isStringLabel && isLabelTruncated && label,
                  preferredPlacement: PopoverPlacement.TOP,
                  children: /* @__PURE__ */ jsx(
                    OptionLabel,
                    {
                      ref: labelRef,
                      $shouldTruncate: truncateTitle,
                      $isHighlighted: isHighlighted,
                      $maxTextLines: MAX_TEXT_LINES,
                      children: label
                    }
                  )
                }
              ),
              rightIcon && /* @__PURE__ */ jsx(
                OptionIcon,
                {
                  iconName: rightIcon.name,
                  $isHighlighted: isHighlighted,
                  $color: rightIcon == null ? void 0 : rightIcon.color
                }
              ),
              children
            ] }),
            breadcrumbs && /* @__PURE__ */ jsx(Breadcrumbs, { breadcrumbs }),
            description && /* @__PURE__ */ jsx(
              OptionDescription,
              {
                $shouldTruncate: truncateDescription,
                $isDisabled: isDisabled,
                children: description
              }
            )
          ] }),
          rightContent
        ]
      }
    ),
    rightContent && isDisabled && keepRightContentActive && /* @__PURE__ */ jsx(ActiveContentWrapper, { children: rightContent })
  ] });
  if (!popoverProps) return content;
  return /* @__PURE__ */ jsx(WithPopover, { ...popoverProps, children: content });
});
const DropdownMenuOptionWithContent = forwardRef((props, ref) => {
  const {
    label,
    isDisabled,
    isFocused,
    isHovered,
    isSelected,
    ariaLabel,
    clickId,
    testId,
    intercomTarget,
    isHighlighted,
    buttonType,
    onClick,
    menuOptionWrapperProps,
    customData
  } = props;
  return /* @__PURE__ */ jsx(MenuOptionOuterWrapperWithContent, { children: /* @__PURE__ */ jsx(
    MenuOptionWrapper,
    {
      role: "option",
      "aria-label": ariaLabel,
      ref,
      $hasLeftIcon: false,
      $isDisabled: isDisabled,
      $isFocused: isFocused,
      $isHovered: isHovered,
      $isSelected: isSelected,
      $isClickable: Boolean(onClick),
      "data-click-id": clickId,
      "data-test-id": testId,
      "data-intercom-target": intercomTarget || label,
      $isHighlighted: isHighlighted,
      $buttonType: buttonType,
      onClick: !isDisabled ? onClick : void 0,
      ...menuOptionWrapperProps,
      children: /* @__PURE__ */ jsx(CustomOption, { customData })
    }
  ) });
});
DropdownMenuOption.displayName = "DropdownMenuOption";
const DropdownMenuSection = ({
  title,
  description,
  options = [],
  truncateDescription,
  truncateTitle
}) => /* @__PURE__ */ jsxs(Fragment, { children: [
  title && /* @__PURE__ */ jsx(DropdownMenuSectionTitle, { $shouldTruncate: truncateTitle, children: title }),
  description && /* @__PURE__ */ jsx(OptionDescription, { $shouldTruncate: truncateDescription, children: description }),
  options.map((optionProps, index) => /* @__PURE__ */ jsx(DropdownMenuOption, { ...optionProps }, index)),
  (title || description || Boolean(options.length)) && /* @__PURE__ */ jsx(DropdownMenuSectionSplit, {})
] });
const DropdownMenu = forwardRef(
  ({
    minDropdownSize,
    maxDropdownSize,
    children,
    headerContent,
    footerContent,
    ...props
  }, ref) => {
    const dropdownSize = !minDropdownSize && !maxDropdownSize ? props.dropdownSize || DropdownSize.S : DropdownSize.AUTO;
    const isVisible = useIsMounted();
    return /* @__PURE__ */ jsxs(
      DropdownMenuWrapper,
      {
        ref,
        $dropdownSize: dropdownSize,
        $minDropdownSize: minDropdownSize,
        $maxDropdownSize: maxDropdownSize,
        $isVisible: isVisible || window.IS_E2E_TESTS || false,
        children: [
          headerContent && /* @__PURE__ */ jsx(DropdownMenuHeaderWrapper, { children: headerContent }),
          /* @__PURE__ */ jsxs(DropdownMenuBodyWrapper, { children: [
            /* @__PURE__ */ jsx(DropdownMenuSection, { ...props }),
            children
          ] }),
          footerContent && /* @__PURE__ */ jsx(DropdownMenuHeaderWrapper, { children: footerContent })
        ]
      }
    );
  }
);
DropdownMenu.displayName = "DropdownMenu";
export {
  Breadcrumbs,
  DropdownActionStyle,
  DropdownButtonOptionType,
  DropdownFooterActionSpace,
  DropdownMenu,
  DropdownMenuBodyWrapper,
  DropdownMenuHeaderWrapper,
  DropdownMenuOption,
  DropdownMenuOptionWithContent,
  DropdownMenuOptionWithOptions,
  DropdownMenuSection,
  DropdownMenuSectionSplit,
  DropdownMenuSectionTitle,
  DropdownMenuSectionWrapper,
  DropdownMenuWrapper,
  DropdownSize,
  MENU_OPTION_BODY_SELECTOR,
  dropdownMenuWrapperCSS
};
//# sourceMappingURL=index.js.map
